<script>
  import { fly } from "svelte/transition";
  import Header from "lib/components/Header.svelte";
  import { recordingStore } from "@stores/recordingStore.js";
  import {
    Accordion,
    Tooltip,
    InfoBlock,
    SectionWrapper,
  } from "paxray-components-svelte";
  import ContentSwitcher from "@components/ContentSwitcher.svelte";
  import ButtonIconOnly from "@components/buttonIconOnly.svelte";
  import LineChartNeu from "@components/LineChartNeu.svelte";
  import LFilters from "../components/LFilters.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { Loading, Search } from "carbon-components-svelte";
  import { getContext, onMount } from "svelte";
  import SideKPI from "@components/sideKPI.svelte";
  import SearchableList from "@components/SearchableList.svelte";
  import ButtonNew from "@components/ButtonNew.svelte";
  import Button from "@components/Button.svelte";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";

  export let idproject;

  const session = getContext("session");
  const dateOfInformation = new Date();
  const step = PROJECT_STAGES.TASK_REGISTRATION.steps.REG_OVERVIEW;
  const stage = PROJECT_STAGES.TASK_REGISTRATION.id;
  let sufficientNumberOfRegistrations = 10;

  function formatNewestDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  function getDateSortValue(value) {
    if (!value) return -Infinity;
    const [day, month, year] = String(value).split(".").map(Number);
    if (!day || !month || !year) return -Infinity;
    return new Date(2000 + year, month - 1, day).getTime();
  }

  function sortDateStrings(a, b) {
    return getDateSortValue(a) - getDateSortValue(b);
  }

  const tableHeaders = [
    { key: "team", value: "Team" },
    { key: "l1", value: "Process (L1)" },
    { key: "l2", value: "Subprocess (L2)" },
    { key: "l3", value: "Task (L3)" },
    { key: "l4", value: "Subtask (L4)" },
    // { key: "topApp", value: "Top App" },
    // { key: "topAppPct", value: "%-TopApp" },
    { key: "nRecordings", value: "#" },
    { key: "newestRecording", value: "Newest", sort: sortDateStrings },
  ];

  let selectAssignedOptions = [
    { key: "enough_registrations", value: "10+ registrations" },
    { key: "limited_registrations", value: "1-9 registrations" },
    { key: "no_registrations", value: "No registrations" },
  ];
  let selectedAssignedOption = "";
  let weeklyChartData;
  let tablePagination = { page: 1, pageSize: 10 };
  let tableSearchTerm = "";
  let loadingOverview = false;
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTeam = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let fullscreen = false;

  let selectMode = 0;
  let lineChart;
  let totalRegistrations;
  let last7DaysRegistrations;
  let unassignedRegistrations;
  let coverageValue;
  let coverageValues = [];
  let selectedCoverageLevel = 0;
  let loadingData = false;
  let loadedDataBeforeChart = true;
  let listOfCoverage = [];
  let displayedListOfCoverage = [];
  let loadingCoverage = false;
  let teamOverview = [];
  let displayedTeamOverview = [];
  let teams = [];
  let filterTeamOptions = [];
  let levelNames = ["L4", "L3", "L2", "L1"];
  let loading = false;
  let loadingChart = true;
  let coverageSorterByNumber = "asc"; //"", "desc, "asc"
  let coverageSorterByName = ""; //"", "desc, "asc"
  let teamSorterByNumber = "asc"; //"", "desc, "asc"
  let teamSorterByName = ""; //"", "desc, "asc"
  let dataInflowOpen = true;
  let coverageOpen = false;
  let teamOverviewOpen = false;
  let dashboardPanelsBeforeGuide;
  let dashboardGuideActive = false;
  let showDashboardGuide = false;
  let dashboardGuideStep = 0;
  let dashboardGuideSteps = [];

  onMount(() => registerViewTour(startDashboardGuide));

  $: if (dashboardGuideActive) {
    if (showDashboardGuide) {
      const panel = dashboardGuideSteps[dashboardGuideStep]?.panel;
      dataInflowOpen = panel === "inflow";
      coverageOpen = panel === "coverage";
      teamOverviewOpen = panel === "teams";
    } else {
      ({ dataInflowOpen, coverageOpen, teamOverviewOpen } =
        dashboardPanelsBeforeGuide);
      dashboardGuideActive = false;
    }
  }

  function startDashboardGuide() {
    if (
      dashboardGuideActive ||
      loadingOverview ||
      loadingData ||
      loadingCoverage
    )
      return;
    dashboardPanelsBeforeGuide = {
      dataInflowOpen,
      coverageOpen,
      teamOverviewOpen,
    };
    dashboardGuideSteps = [
      {
        target: '[data-guide="dashboard-inflow"]',
        title: "Review registration progress",
        panel: "inflow",
        text: "Use this dashboard to see which tasks your teams have registered and where more examples may be needed. “Data Inflow” shows total registrations, registrations from the last seven days and registrations that have not been assigned to a subtask.",
      },
      {
        target: '[data-guide="dashboard-filters"]',
        title: "Focus on the relevant work",
        text: "Filter by team, process, subprocess, task or subtask. Use the registration-count filter to find items with no registrations, 1–9 registrations or 10+ registrations. Use search to find specific entries.",
      },
      {
        target: '[data-guide="dashboard-table"]',
        title: "Read the registration overview",
        text: "Each row shows a team and its assigned work. “#” shows the number of registrations, and “Newest” shows the date of the latest registration. Sort the columns to find gaps or check recent activity.",
      },
      {
        target: '[data-guide="dashboard-coverage"]',
        title: "Check coverage across process levels",
        panel: "coverage",
        text: "Open “Coverage” to see which parts of your project have registrations assigned. Switch between L4, L3, L2 and L1 to review subtasks, tasks, subprocesses and processes. Use the counts to identify areas that still need examples.",
      },
      {
        target: '[data-guide="dashboard-teams"]',
        title: "Review team coverage",
        panel: "teams",
        text: "Open “Team Overview” to compare registrations per team member within the selected filters. Search for a team or sort the list by name or count to find teams that may need support.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    dashboardGuideStep = 0;
    showDashboardGuide = dashboardGuideSteps.length > 0;
    dashboardGuideActive = true;
  }

  let lFiltersRef;
  $: breadcrumbsClickable = session?.isValid === true;
  $: dateOfInformationShort = dateOfInformation.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  });
  $: dateOfInformationFull = dateOfInformation.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  $: ({
    processes,
    listOfSubprocesses,
    listOfTasks,
    listOfSubtasks,
    listOfRecordings: rawListOfRecordings,
    listOfTeams,
  } = $recordingStore);

  $: listOfRecordings = (rawListOfRecordings || []).map((recording) => ({
    ...recording,
    recorderid: recording.recorderid ?? recording.recording_id,
    teamid: recording.teamid ?? recording.team_id,
    starttimeutc: recording.starttimeutc ?? recording.start_time,
    endtimeutc: recording.endtimeutc ?? recording.end_time,
  }));

  $: console.log({
    listOfRecordings,
    filteredListofRecordings,
    listOfSubtasks,
  });
  $: filteredListofRecordings = setFilteredListofRecordings(
    listOfRecordings,
    listOfSubtasks,
    listOfTasks,
    listOfSubprocesses,
    processes,
    selectedProcess,
    selectedSubprocess,
    selectedTask,
    selectedSubtask,
    selectedTeam,
    selectedAssignedOption,
  );

  $: {
    listOfSubtasks;
    getDataInflowKPIs(filteredListofRecordings);
  }

  $: loadingCoverage =
    !filteredListofRecordings ||
    filteredListofRecordings.length === 0 ||
    listOfCoverage.length === 0;
  $: listOfCoverage =
    (filteredListofRecordings,
    Array.from(getCoverageList(3 - selectedCoverageLevel).values()));

  //$: displayedListOfCoverage = listOfCoverage;

  //$: coverageValue = coverageValues[selectedCoverageLevel] ?? "";

  $: teamOverview = getTeamOverview(teams, filteredListofRecordings);
  //$: displayedTeamOverview = teamOverview;
  //$: setDisplayedTeamOverview(teamOverview);

  $: coverageValue = setCoverageValues(
    selectedCoverageLevel,
    listOfSubtasks,
    filteredListofRecordings,
  );

  $: tableRows = (() => {
    if (!listOfSubtasks || !filteredListofRecordings) return [];
    // Teams aus "Set Up Teams", damit Zeilen ohne Registrierung ihr Team zeigen.
    const assignedTeamNames = (subtaskId) =>
      filterTeamOptions
        .filter((team) => team.subtaskIds?.has(String(subtaskId)))
        .map((team) => team.name)
        .join(", ");
    const UNASSIGNED_KEY = "__unassigned__";
    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];
    const rowMap = new Map();
    uniqueRecordings.forEach((recording) => {
      const subtask = listOfSubtasks.find((st) =>
        st.recordings?.some((r) => r.recording_id === recording.recorderid),
      );
      let task = null;
      let subprocess = null;
      let process = null;
      if (subtask) {
        task = listOfTasks.find((t) => t.id === subtask.task_id);
        if (task) {
          subprocess = listOfSubprocesses.find(
            (sp) => sp.id === task.subprocess_id,
          );
          if (subprocess) {
            process = processes.find((p) => p.id === subprocess.process_id);
          }
        }
      }
      const key = subtask?.id ?? UNASSIGNED_KEY;
      const recordingDate = recording.endtimeutc
        ? new Date(recording.endtimeutc)
        : null;
      if (rowMap.has(key)) {
        const existing = rowMap.get(key);
        existing.count++;
        if (
          recordingDate &&
          !isNaN(recordingDate.getTime()) &&
          (!existing.newestDate || recordingDate > existing.newestDate)
        ) {
          existing.newestDate = recordingDate;
        }
        if (recording.teamid) existing.teamIds.add(recording.teamid);
      } else {
        const teamIds = new Set();
        if (recording.teamid) teamIds.add(recording.teamid);
        rowMap.set(key, {
          id: key,
          team: recording.team ?? "",
          l1: process?.name ?? "",
          l2: subprocess?.name ?? "",
          l3: task?.name ?? "",
          l4: subtask?.name ?? "",
          count: 1,
          newestDate:
            recordingDate && !isNaN(recordingDate.getTime())
              ? recordingDate
              : null,
          processid: process?.id ?? null,
          subprocessid: subprocess?.id ?? null,
          taskid: task?.id ?? null,
          subtaskid: subtask?.id ?? null,
          teamIds,
        });
      }
    });

    const filteredSubtasksForTable = getFilteredItemsAtLevel(3);
    filteredSubtasksForTable.forEach((subtask) => {
      if (rowMap.has(subtask.id)) return; // already has recordings
      const task = listOfTasks?.find((t) => t.id === subtask.task_id) ?? null;
      const subprocess = task
        ? (listOfSubprocesses?.find((sp) => sp.id === task.subprocess_id) ??
          null)
        : null;
      const process = subprocess
        ? (processes?.find((p) => p.id === subprocess.process_id) ?? null)
        : null;
      rowMap.set(subtask.id, {
        id: subtask.id,
        team: assignedTeamNames(subtask.id),
        l1: process?.name ?? "",
        l2: subprocess?.name ?? "",
        l3: task?.name ?? "",
        l4: subtask?.name ?? "",
        count: 0,
        newestDate: null,
        processid: process?.id ?? null,
        subprocessid: subprocess?.id ?? null,
        taskid: task?.id ?? null,
        subtaskid: subtask?.id ?? null,
        teamIds: new Set(),
      });
    });

    const assignedRows = Array.from(rowMap.values()).filter(
      (row) => row.id !== UNASSIGNED_KEY,
    );
    const unassignedRow = rowMap.get(UNASSIGNED_KEY);

    const filteredAssigned = assignedRows
      .filter((row) => {
        if (selectedAssignedOption === "enough_registrations") {
          return row.count >= sufficientNumberOfRegistrations;
        }
        if (selectedAssignedOption === "limited_registrations") {
          return row.count > 0 && row.count < sufficientNumberOfRegistrations;
        }
        if (selectedAssignedOption === "no_registrations")
          return row.count === 0;
        return true;
      })
      // .filter((row) => {
      //   const matchProcess =
      //     !selectedProcess ||
      //     selectedProcess === "Process" ||
      //     row.processid === selectedProcess;
      //   const matchSubprocess =
      //     !selectedSubprocess ||
      //     selectedSubprocess === "Subprocess" ||
      //     row.subprocessid === selectedSubprocess;
      //   const matchTask =
      //     !selectedTask ||
      //     selectedTask === "Task" ||
      //     row.taskid === selectedTask;
      //   const matchSubtask =
      //     !selectedSubtask ||
      //     selectedSubtask === "Subtask" ||
      //     row.subtaskid === selectedSubtask;
      //   const matchTeam =
      //     !selectedTeam ||
      //     selectedTeam === "Team" ||
      //     row.teamIds.has(selectedTeam);
      //   return (
      //     matchProcess &&
      //     matchSubprocess &&
      //     matchTask &&
      //     matchSubtask &&
      //     matchTeam
      //   );
      // })
      .map((row) => ({
        id: row.id,
        team: row.team,
        l1: row.l1,
        l2: row.l2,
        l3: row.l3,
        l4: row.l4,
        topApp: "",
        topAppPct: "",
        nRecordings: row.count,
        newestRecording: row.newestDate ? formatNewestDate(row.newestDate) : "",
      }));

    return filteredAssigned;
  })();

  $: filteredTableRows = (
    tableSearchTerm
      ? tableRows.filter((row) =>
          [row.l1, row.l2, row.l3, row.l4, row.newestRecording].some((v) =>
            v?.toLowerCase().includes(tableSearchTerm.toLowerCase()),
          ),
        )
      : tableRows
  )
    .slice()
    .sort((a, b) => b.nRecordings - a.nRecordings);

  $: sortCoverageByName(coverageSorterByName, listOfCoverage);
  $: sortCoverageByNumber(coverageSorterByNumber, listOfCoverage);
  $: sortTeamByName(teamSorterByName, teamOverview);
  $: sortTeamByNumber(teamSorterByNumber, teamOverview);

  $: setWeeklyChartData(filteredListofRecordings, selectMode);

  // $: weeklyChartData = (() => {
  //   if (!filteredListofRecordings || filteredListofRecordings.length === 0) return [];
  //   const weekMap = new Map();
  //   filteredListofRecordings.forEach((rec) => {
  //     if (!rec?.recorderid || rec.deleted) return;
  //     const monday = getMondayISO(rec.starttimeutc);
  //     weekMap.set(monday, (weekMap.get(monday) || 0) + 1);
  //   });
  //   const sorted = [...weekMap.entries()].sort(([a], [b]) =>
  //     a.localeCompare(b),
  //   );
  //   return [
  //     {
  //       label: "Registrations",
  //       data: sorted.map(([x, y]) => ({ x, y })),
  //     },
  //   ];
  // })();

  function setCoverageValues() {
    let total = listOfCoverage.reduce((sum, item) => {
      if (
        !selectedCoverageLevel &&
        item.count >= sufficientNumberOfRegistrations
      )
        sum++;
      else if (
        selectedCoverageLevel &&
        !item.values.some((value) => value < sufficientNumberOfRegistrations)
      )
        sum++;
      return sum;
    }, 0);

    return ((100 * total) / listOfCoverage.length || 0).toFixed(0) + "%";
  }

  function setColorValues(values) {
    let total = values.length;

    let red = (100 * values.filter((v) => v === 0).length) / total;
    let yellow =
      (100 *
        values.filter((v) => v > 0 && v < sufficientNumberOfRegistrations)
          .length) /
      total;
    let green =
      (100 *
        values.filter((v) => v >= sufficientNumberOfRegistrations).length) /
      total;

    return [
      { color: "#f08e90", percent: red },
      { color: "#e9f08e", percent: yellow },
      { color: "#8ef095", percent: green },
    ];
  }

  function sortCoverageByName(coverageSorterByName, listOfCoverage) {
    if (!coverageSorterByName || !listOfCoverage) return;
    displayedListOfCoverage = [...listOfCoverage].sort((a, b) => {
      if (coverageSorterByName === "asc") {
        return a.name.localeCompare(b.name);
      } else if (coverageSorterByName === "desc") {
        return b.name.localeCompare(a.name);
      } else {
        return 0;
      }
    });
  }

  function sortCoverageByNumber(coverageSorterByNumber, listOfCoverage) {
    if (!coverageSorterByNumber || !listOfCoverage) return;
    displayedListOfCoverage = [...listOfCoverage].sort((a, b) => {
      if (coverageSorterByNumber === "asc") {
        return a.count - b.count;
      } else if (coverageSorterByNumber === "desc") {
        return b.count - a.count;
      } else {
        return 0;
      }
    });
  }

  function sortTeamByNumber(teamSorterByNumber, teamOverview) {
    if (!teamSorterByNumber || !teamOverview) return;

    displayedTeamOverview = [...teamOverview].sort((a, b) => {
      if (teamSorterByNumber === "asc") {
        return a.ratio - b.ratio;
      } else if (teamSorterByNumber === "desc") {
        return b.ratio - a.ratio;
      } else {
        return 0;
      }
    });
  }

  function sortTeamByName(teamSorterByName, teamOverview) {
    if (!teamSorterByName || !teamOverview) return;
    displayedTeamOverview = [...teamOverview].sort((a, b) => {
      if (teamSorterByName === "asc") {
        return a.name.localeCompare(b.name);
      } else if (teamSorterByName === "desc") {
        return b.name.localeCompare(a.name);
      } else {
        return 0;
      }
    });
  }

  async function getTeamInfo(idproject) {
    try {
      const res = await fetchWrapper(
        `/api/damprojects/dam-teams?idproject=${idproject}`,
        { method: "GET" },
      );
      const data = await res.json();
      teams = data.map((team) => ({
        ...team,
        damUsers: team.damUsers.map((user) => user.id),
        subprocesses: team.subprocesses.map((subprocess) => subprocess.id),
      }));
      return teams;
    } catch (err) {
      console.error(err);
    }
  }

  function getDataInflowKPIs() {
    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];

    totalRegistrations = uniqueRecordings.length;

    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    last7DaysRegistrations = uniqueRecordings.filter((rec) => {
      const recDate = new Date(rec.endtimeutc);
      return recDate >= sevenDaysAgo && recDate <= now;
    }).length;

    unassignedRegistrations = uniqueRecordings.filter((rec) => {
      const subtask = listOfSubtasks.find((t) =>
        t.recordings?.some((r) => r.recording_id === rec.recorderid),
      );
      return !subtask;
    }).length;

    //getCoverageValues();
    loadingData = false;
  }

  function getFilteredItemsAtLevel(level) {
    // Step 1: Apply each direct filter independently
    let filteredSubtasks = listOfSubtasks;
    if (selectedSubtask && selectedSubtask !== "Subtask") {
      filteredSubtasks = filteredSubtasks.filter(
        (st) => st.id === selectedSubtask,
      );
    }

    // Filter subtasks by team: keep subtasks assigned to the team in "Set Up Teams"
    // and subtasks that have recordings from the selected team
    if (selectedTeam && selectedTeam !== "Team") {
      const teamRecorderIds = new Set(
        (filteredListofRecordings || [])
          .filter((r) => r.teamid === selectedTeam)
          .map((r) => r.recorderid),
      );
      const teamSubtaskIds =
        filterTeamOptions.find((team) => String(team.id) === String(selectedTeam))
          ?.subtaskIds || new Set();
      filteredSubtasks = filteredSubtasks.filter(
        (st) =>
          teamSubtaskIds.has(String(st.id)) ||
          st.recordings?.some((r) => teamRecorderIds.has(r.recording_id)),
      );
    }

    let filteredTasks = listOfTasks;
    if (selectedTask && selectedTask !== "Task") {
      filteredTasks = filteredTasks.filter((t) => t.id === selectedTask);
    }

    let filteredSubprocesses = listOfSubprocesses;
    if (selectedSubprocess && selectedSubprocess !== "Subprocess") {
      filteredSubprocesses = filteredSubprocesses.filter(
        (sp) => sp.id === selectedSubprocess,
      );
    }

    let filteredProcesses = processes;
    if (selectedProcess && selectedProcess !== "Process") {
      filteredProcesses = filteredProcesses.filter(
        (p) => p.id === selectedProcess,
      );
    }

    // Step 2: Cascade top-down (parent restricts children)
    filteredSubprocesses = filteredSubprocesses.filter((sp) =>
      filteredProcesses.some((p) => p.id === sp.process_id),
    );
    filteredTasks = filteredTasks.filter((t) =>
      filteredSubprocesses.some((sp) => sp.id === t.subprocess_id),
    );
    filteredSubtasks = filteredSubtasks.filter((st) =>
      filteredTasks.some((t) => t.id === st.task_id),
    );

    // Step 3: Cascade bottom-up (children restrict parents)
    filteredTasks = filteredTasks.filter((t) =>
      filteredSubtasks.some((st) => st.task_id === t.id),
    );
    filteredSubprocesses = filteredSubprocesses.filter((sp) =>
      filteredTasks.some((t) => t.subprocess_id === sp.id),
    );
    filteredProcesses = filteredProcesses.filter((p) =>
      filteredSubprocesses.some((sp) => sp.process_id === p.id),
    );

    if (level === 0) return filteredProcesses;
    if (level === 1) return filteredSubprocesses;
    if (level === 2) return filteredTasks;
    return filteredSubtasks;
  }

  function getCoverageList(level) {
    if (!filteredListofRecordings || !listOfSubtasks) return [];
    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];

    const levelMap = new Map(); // id → { name, count }

    // Pre-populate the map with filtered items at the requested level, count = 0
    const filteredItems = getFilteredItemsAtLevel(level);
    filteredItems.forEach((item) =>
      levelMap.set(item.id, { name: item.name, count: 0, values: [] }),
    );

    uniqueRecordings.forEach((recording) => {
      const subtask = listOfSubtasks.find((t) =>
        t.recordings?.some((r) => r.recording_id === recording.recorderid),
      );
      if (!subtask) return;

      const task = listOfTasks.find((t) => t.id === subtask.task_id);
      const subprocess = task
        ? listOfSubprocesses.find((sp) => sp.id === task.subprocess_id)
        : null;
      const process = subprocess
        ? processes.find((p) => p.id === subprocess.process_id)
        : null;

      let key;
      if (level === 0) {
        if (!process) return;
        key = process.id;
      } else if (level === 1) {
        if (!subprocess) return;
        key = subprocess.id;
      } else if (level === 2) {
        if (!task) return;
        key = task.id;
      } else {
        key = subtask.id;
      }

      if (levelMap.has(key)) {
        levelMap.get(key).count++;
      }
    });

    if (level < 3) {
      let lowerLevelMap = getCoverageList(level + 1);
      levelMap.forEach((item, id) => {
        const process = processes.find((p) => p.id === id);
        const subprocess = listOfSubprocesses.find((sp) => sp.id === id);
        const task = listOfTasks.find((t) => t.id === id);
        if (process) {
          process.subprocesses.forEach((sp) => {
            if (lowerLevelMap.has(sp.id)) {
              item.values.push(lowerLevelMap.get(sp.id).count);
            }
          });
        }
        if (subprocess) {
          subprocess.tasks.forEach((t) => {
            if (lowerLevelMap.has(t.id)) {
              item.values.push(lowerLevelMap.get(t.id).count);
            }
          });
        }
        if (task) {
          task.subtasks.forEach((st) => {
            if (lowerLevelMap.has(st.id)) {
              item.values.push(lowerLevelMap.get(st.id).count);
            }
          });
        }
      });
    }

    loadingCoverage = false;
    return levelMap;
  }

  function getTeamOverview(teams, filteredListofRecordings) {
    if (!teams?.length || !filteredListofRecordings) return [];

    const teamSizes = new Map(); // teamId → number of users
    const teamNames = new Map(); // teamId → name
    teams.forEach((team) => {
      teamNames.set(team.id, team.name);
      teamSizes.set(team.id, team.damUsers ? team.damUsers.length : 0);
    });

    const teamMap = new Map(
      teams.map((team) => [
        team.id,
        { name: team.name, count: 0, teamid: team.id },
      ]),
    );
    filteredListofRecordings.forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      const teamid = recording.teamid;
      if (!teamid) return;

      if (teamMap.has(teamid)) {
        teamMap.get(teamid).count++;
      } else {
        teamMap.set(teamid, {
          name: teamNames.get(teamid) ?? recording.team ?? "Unknown",
          count: 1,
          teamid,
        });
      }
    });
    return Array.from(teamMap.values()).map((team) => {
      const userscount = teamSizes.get(team.teamid) || 0;
      return {
        name: team.name,
        userscount,
        registerscount: team.count,
        ratio:
          userscount > 0 ? parseFloat((team.count / userscount).toFixed(2)) : 0,
      };
    });
  }

  function getCoverageValues() {
    if (!filteredListofRecordings || !listOfSubtasks) return;

    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];

    // Sets of IDs at each level that have at least one recording assigned
    const withRecordings = [new Set(), new Set(), new Set(), new Set()]; // [L1, L2, L3, L4]

    uniqueRecordings.forEach((recording) => {
      const subtask = listOfSubtasks.find((st) =>
        st.recordings?.some((r) => r.recording_id === recording.recorderid),
      );
      if (!subtask) return;
      withRecordings[0].add(subtask.id);

      const task = listOfTasks.find((t) => t.id === subtask.task_id);
      if (!task) return;
      withRecordings[1].add(task.id);

      const subprocess = listOfSubprocesses.find(
        (sp) => sp.id === task.subprocess_id,
      );
      if (!subprocess) return;
      withRecordings[2].add(subprocess.id);

      const process = processes.find((p) => p.id === subprocess.process_id);
      if (!process) return;
      withRecordings[3].add(process.id);
    });

    const filteredIds = [
      new Set(getFilteredItemsAtLevel(3).map((item) => item.id)),
      new Set(getFilteredItemsAtLevel(2).map((item) => item.id)),
      new Set(getFilteredItemsAtLevel(1).map((item) => item.id)),
      new Set(getFilteredItemsAtLevel(0).map((item) => item.id)),
    ];

    coverageValues = filteredIds.map((ids, i) => {
      const total = ids.size;
      if (total === 0) return "N/A";
      const covered = [...withRecordings[i]].filter((id) => ids.has(id)).length;
      return `${Math.round((covered / total) * 100)}%`;
    });
  }

  // function handleRefresh() {
  //   console.log("Refresh button clicked");
  // }

  function getMondayISO(dateStr) {
    const d = new Date(dateStr);
    const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setUTCDate(d.getUTCDate() + diff);
    return d.toISOString().slice(0, 10);
  }

  function setWeeklyChartData() {
    if (
      !filteredListofRecordings ||
      filteredListofRecordings.length === 0 ||
      loadedDataBeforeChart ||
      selectMode !== 1
    ) {
      weeklyChartData = [];
      // ohne Registrierungen sonst endloser Spinner (loadingChart startet mit true)
      if (!loadedDataBeforeChart) loadingChart = false;
      return;
    }
    loadingChart = true;
    const weekMap = new Map();
    filteredListofRecordings.forEach((rec) => {
      if (!rec?.recorderid || rec.deleted) return;
      const monday = getMondayISO(rec.starttimeutc);
      weekMap.set(monday, (weekMap.get(monday) || 0) + 1);
    });
    const sorted = [...weekMap.entries()].sort(([a], [b]) =>
      a.localeCompare(b),
    );
    weeklyChartData = [
      {
        label: "Registrations",
        data: sorted.map(([x, y]) => ({ x, y })),
      },
    ];
    loadingChart = false;
  }

  function setFilteredListofRecordings() {
    return (
      listOfRecordings &&
      listOfRecordings.filter((rec) => {
        const subtask = listOfSubtasks.find((st) =>
          st.recordings?.some((r) => r.recording_id === rec.recorderid),
        );

        let task = null;
        let subprocess = null;
        let process = null;

        if (subtask) {
          task = listOfTasks.find((t) => t.id === subtask.task_id);
          if (task) {
            subprocess = listOfSubprocesses.find(
              (sp) => sp.id === task.subprocess_id,
            );
            if (subprocess) {
              process = processes.find((p) => p.id === subprocess.process_id);
            }
          }
        }

        const matchProcess =
          !selectedProcess ||
          selectedProcess === "Process" ||
          process?.id === selectedProcess;
        const matchSubprocess =
          !selectedSubprocess ||
          selectedSubprocess === "Subprocess" ||
          subprocess?.id === selectedSubprocess;
        const matchTask =
          !selectedTask || selectedTask === "Task" || task?.id === selectedTask;
        const matchSubtask =
          !selectedSubtask ||
          selectedSubtask === "Subtask" ||
          subtask?.id === selectedSubtask;
        const matchTeam =
          !selectedTeam ||
          selectedTeam === "Team" ||
          rec.teamid === selectedTeam;

        return (
          matchProcess &&
          matchSubprocess &&
          matchTask &&
          matchSubtask &&
          matchTeam
        );
      })
    );
  }

  function handleMarkAsCompleted() {
    console.log("Marking as completed...");
  }

  onMount(async () => {
    await getTeamInfo(idproject);
    loadedDataBeforeChart = false;
    setWeeklyChartData();
  });
</script>

<Header
  section="Project Progress"
  area="Discovery"
  breadcrumb="Registration Dashboard"
  tooltipText="Monitor task registration progress, coverage, and activity across teams and registered processes."
  {breadcrumbsClickable}
>
  <!-- <Button text="Refresh" on:click={handleRefresh} variant="secondary" /> -->
  <div class="dashboard-switcher">
    <ContentSwitcher
      bind:selected={selectMode}
      options={["Table", "Chart"]}
      fitContent={true}
    ></ContentSwitcher>
  </div>
  <ProjectStepActions
    {idproject}
    {step}
    {stage}
    showGoBackButton={false}
    showMarkAsCompletedButton={false}
    showCompletedState={false}
    text="Complete Registration Phase"
    completionWarning="This marks the task registration phase as complete. Please make sure enough registrations have been collected before continuing. This action cannot be undone."
  />
</Header>

<div class:table_section={selectMode === 0}>
  <SectionWrapper
    {fullscreen}
    fitScreen={selectMode === 1}
    seamless={true}
    seamlessPadding={true}
  >
    <div slot="main" class="main_wrapper">
      <div
        class="select_wrapper"
        data-guide="dashboard-filters"
        style={loadingOverview ? "visibility: hidden;" : ""}
      >
        <div class="filter_wrapper">
          <LFilters
            bind:this={lFiltersRef}
            {idproject}
            showAssignedFilter={true}
            {selectAssignedOptions}
            bind:selectedProcess
            bind:selectedSubprocess
            bind:selectedTask
            bind:selectedSubtask
            bind:selectedTeam
            bind:selectedAssignedOption
            bind:teamOptions={filterTeamOptions}
          >
            <svelte:fragment slot="before-filters">
              {#if selectMode === 0}
                <div class="filter_field dashboard_search">
                  <span class="search_label">Search</span>
                  <Search
                    size="lg"
                    bind:value={tableSearchTerm}
                    labelText="Search registrations"
                    placeholder="Search..."
                  />
                </div>
              {/if}
            </svelte:fragment>
          </LFilters>
        </div>
      </div>
      <div class="canvas" class:table_canvas={selectMode === 0}>
        <div class="ui_viewer_wrapper" class:table_ui_viewer={selectMode === 0}>
          {#if loading}
            <div class="loading_wrapper">
              <Loading withOverlay={false} />
            </div>
          {:else if selectMode === 0}
            <div class="table_view_wrapper" data-guide="dashboard-table">
              <DataTableBlack
                headers={tableHeaders}
                rows={filteredTableRows}
                sortable
                slicePage
                bind:page={tablePagination.page}
                bind:pageSize={tablePagination.pageSize}
                pageSizes={[10, 25, 50, 75, 100]}
                showNewButton={false}
                showToolbar={false}
                totalItems={filteredTableRows.length}
              >
                <svelte:fragment slot="cell" let:cell>
                  <div
                    class="cell_content"
                    class:newest_cell={cell.key === "newestRecording"}
                    title={String(cell.value ?? "")}
                  >
                    {cell.value ?? ""}
                  </div>
                </svelte:fragment>
              </DataTableBlack>
            </div>
            <!-- {:else if weeklyChartData.length > 0 && weeklyChartData[0]?.data?.length > 0} -->
          {:else if loadingChart === false}
            <LineChartNeu
              bind:this={lineChart}
              height="100%"
              width="100%"
              variant="clean"
              axes={["y"]}
              dataSets={weeklyChartData}
              displayLegend={false}
              noDecimals={true}
            />
          {:else}
            <div class="loading_wrapper">
              <Loading withOverlay={false} />
            </div>
          {/if}
        </div>
      </div>
      <!-- <div class="menu_overlay bottom">
      {#if fullscreen === false}
        <ButtonIconOnly
          icon="fullscreen"
          on:click={() => {
            fullscreen = true;
          }}
        />
      {:else}
        <ButtonIconOnly
          icon="fullscreen_off"
          on:click={() => {
            fullscreen = false;
          }}
        />
      {/if}
    </div> -->
    </div>

    <div slot="menu" class="menu">
      <div data-guide="dashboard-inflow">
        <Accordion
          heading="Data Inflow"
          bind:open={dataInflowOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <div class="dataInflow_wrapper">
            <SideKPI
              name="Total Registrations"
              value={totalRegistrations || 0}
              loading={loadingData}
            ></SideKPI>
            <SideKPI
              name="Last 7 Days Registrations"
              value={last7DaysRegistrations || 0}
              loading={loadingData}
            ></SideKPI>
            <SideKPI
              name="Unassigned Registrations"
              value={unassignedRegistrations || 0}
              loading={loadingData}
            ></SideKPI>
            <SideKPI
              name="Date of Information"
              value={dateOfInformationShort}
              loading={loadingData}
            >
              <span slot="value" title={dateOfInformationFull}>
                {dateOfInformationShort}
              </span>
            </SideKPI>
          </div>
        </Accordion>
      </div>
      <div data-guide="dashboard-coverage">
        <Accordion
          bind:open={coverageOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <svelte:fragment slot="heading">
            <span class="accordion-heading-with-tooltip">
              Coverage
              <Tooltip>
                <p>
                  This statistic indicates how many elements from the list below
                  have recordings assigned.
                </p>
                <p>
                  <em
                    >(The displayed data is dependent of the filters at the top
                    of this page.)</em
                  >
                </p>
              </Tooltip>
            </span>
          </svelte:fragment>
          <SearchableList
            items={displayedListOfCoverage}
            placeholder="Search"
            loading={loadingCoverage}
          >
            <svelte:fragment slot="middle">
              <div class="switcher_kpi_wrapper">
                <ContentSwitcher
                  bind:selected={selectedCoverageLevel}
                  options={levelNames}
                ></ContentSwitcher>
                <SideKPI
                  name={`${levelNames[selectedCoverageLevel]} Completion`}
                  value={coverageValue}
                  loading={loadingCoverage}
                ></SideKPI>
              </div>
              <div class="shorter-button-wrapper">
                <div
                  class:sorter-button={true}
                  class:sorter-desc={coverageSorterByName === "desc"}
                  class:sorter-asc={coverageSorterByName === "asc"}
                >
                  <ButtonNew
                    setting
                    text="Sort by name"
                    icon={coverageSorterByName ? "sorted-arrow2" : "sort-arrow"}
                    iconRight
                    secondary
                    on:click={() => {
                      coverageSorterByNumber = "";
                      coverageSorterByName =
                        !coverageSorterByName || coverageSorterByName === "desc"
                          ? "asc"
                          : "desc";
                    }}
                  ></ButtonNew>
                </div>
                <div
                  class:sorter-button={true}
                  class:sorter-desc={coverageSorterByNumber === "desc"}
                  class:sorter-asc={coverageSorterByNumber === "asc"}
                >
                  <ButtonNew
                    setting
                    text="Sort by number"
                    icon={coverageSorterByNumber
                      ? "sorted-arrow2"
                      : "sort-arrow"}
                    iconRight
                    secondary
                    on:click={() => {
                      coverageSorterByName = "";
                      coverageSorterByNumber =
                        !coverageSorterByNumber ||
                        coverageSorterByNumber === "desc"
                          ? "asc"
                          : "desc";
                    }}
                  ></ButtonNew>
                </div>
              </div>
            </svelte:fragment>
            <svelte:fragment slot="loading">
              <SideKPI name="" value="" loading={true}></SideKPI>
            </svelte:fragment>
            <svelte:fragment let:item>
              <SideKPI
                name={item.name}
                value={item.count ?? 0}
                colorByValue={true}
                multicolor={selectedCoverageLevel !== 0
                  ? setColorValues(item.values)
                  : null}
              />
            </svelte:fragment>
          </SearchableList>
        </Accordion>
      </div>

      <div data-guide="dashboard-teams">
        <Accordion
          bind:open={teamOverviewOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <svelte:fragment slot="heading">
            <span class="accordion-heading-with-tooltip">
              Team Overview
              <Tooltip>
                <p>
                  This statistic indicates how many recordings have been
                  captured per team member.
                </p>
                <p>
                  <em
                    >(The displayed data is dependent of the filters at the top
                    of this page.)</em
                  >
                </p>
              </Tooltip>
            </span>
          </svelte:fragment>
          <SearchableList
            items={displayedTeamOverview}
            placeholder="Search for Team"
            loading={loadingData}
          >
            <svelte:fragment slot="middle">
              <div class="shorter-button-wrapper">
                <div
                  class:sorter-button={true}
                  class:sorter-desc={teamSorterByName === "desc"}
                  class:sorter-asc={teamSorterByName === "asc"}
                >
                  <ButtonNew
                    setting
                    text="Sort by name"
                    icon={teamSorterByName ? "sorted-arrow2" : "sort-arrow"}
                    iconRight
                    secondary
                    on:click={() => {
                      teamSorterByNumber = "";
                      teamSorterByName =
                        !teamSorterByName || teamSorterByName === "desc"
                          ? "asc"
                          : "desc";
                    }}
                  ></ButtonNew>
                </div>
                <div
                  class:sorter-button={true}
                  class:sorter-desc={teamSorterByNumber === "desc"}
                  class:sorter-asc={teamSorterByNumber === "asc"}
                >
                  <ButtonNew
                    setting
                    text="Sort by number"
                    icon={teamSorterByNumber ? "sorted-arrow2" : "sort-arrow"}
                    iconRight
                    secondary
                    on:click={() => {
                      teamSorterByName = "";
                      teamSorterByNumber =
                        !teamSorterByNumber || teamSorterByNumber === "desc"
                          ? "asc"
                          : "desc";
                    }}
                  ></ButtonNew>
                </div>
              </div>
            </svelte:fragment>
            <svelte:fragment slot="loading">
              <SideKPI name="" value="" loading={true}></SideKPI>
            </svelte:fragment>
            <svelte:fragment let:item>
              <SideKPI name={item.name} value={item.ratio || 0}></SideKPI>
            </svelte:fragment>
          </SearchableList>
        </Accordion>
      </div>
    </div>
  </SectionWrapper>
</div>

<SpotlightGuide
  bind:open={showDashboardGuide}
  bind:currentStep={dashboardGuideStep}
  steps={dashboardGuideSteps}
  allowInteraction={false}
/>

<style>
  .select_wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 0.25rem;
    width: 100%;
  }

  .filter_wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.38rem;
    width: 100%;
    background: var(--pax-white);
    border-radius: 0.5rem;
    padding: 0;
  }

  .filter_wrapper :global(.filter_field) {
    width: 10.5rem;
    min-width: 0;
  }

  .filter_wrapper > :global(.filter_wrapper) {
    width: 100%;
    min-width: 0;
  }

  .filter_wrapper .dashboard_search {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    flex: 1 0 10.5rem;
    width: auto;
  }

  .dashboard_search :global(.bx--search-input) {
    height: 2.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background-color: var(--pax-white);
    color: var(--pax-black);
  }

  .dashboard_search :global(.bx--search-input::placeholder) {
    color: var(--pax-grey);
  }

  .dashboard_search :global(.bx--search-magnifier-icon),
  .dashboard_search :global(.bx--search-close) {
    fill: var(--pax-black);
  }

  .search_label {
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .filter_wrapper :global(.filter_field .custom_select),
  .filter_wrapper :global(.filter_field .bx--form-item),
  .filter_wrapper :global(.filter_field .bx--select-input),
  .filter_wrapper :global(.filter_field .bx--select-input__wrapper) {
    width: 100%;
  }

  .table_section {
    width: 100%;
  }

  .table_section :global(.collapsible_wrapper),
  .table_section :global(.view_wrapper),
  .table_section :global(.view_wrapper > .canvas) {
    height: auto;
    min-height: auto;
  }

  .table_section :global(.view_wrapper > .canvas) {
    align-self: flex-start;
  }

  .canvas {
    flex: 1;
    min-height: 0;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
  }

  .table_canvas {
    flex: 0 0 auto;
    min-height: auto;
    padding: 0;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .main_wrapper {
    position: relative;
    flex: 1;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.5rem;
    min-height: 0;
  }

  .main_wrapper :global(.warning-notice) {
    margin-bottom: 0.5rem;
  }

  /* .menu_overlay {
    width: 100%;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  } */

  .ui_viewer_wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
  }

  .table_ui_viewer {
    flex: 0 0 auto;
    min-height: auto;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }

  .switcher_kpi_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
  }

  .switcher_kpi_wrapper :global(.grid_container) {
    width: 100%;
    height: 3rem !important;
    min-height: 3rem;
    max-height: 3rem;
  }

  .dataInflow_wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .table_view_wrapper {
    flex: 0 0 auto;
    min-height: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    /* overflow-y: auto; */
  }

  .shorter-button-wrapper {
    display: flex;
    justify-content: space-evenly;
  }

  :global(.sorter-asc img) {
    transform: rotate(180deg);
    transform-origin: center;
  }

  .shorter-button-wrapper > .sorter-button > :global(.mainbutton) {
    height: 1rem !important;
    width: 100%;
  }
  .sorter-button {
    width: 49%;
  }

  .accordion-heading-with-tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }
</style>
